class SizeGuideDrawer extends HTMLElement {
  constructor() {
    super();

    this.boundClose = this.close.bind(this);
    this.boundKeyup = this.handleKeyup.bind(this);
    this.boundTriggerClick = this.handleTriggerClick.bind(this);
    this.boundBackdropClick = this.handleBackdropClick.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;

    this.trigger = this.querySelector('[data-size-guide-open]');
    this.template = this.querySelector('[data-size-guide-template]');

    if (!this.trigger || !this.template) return;

    this.mountDrawer();

    if (!this.drawer || !this.dialog) return;

    this.trigger.addEventListener('click', this.boundTriggerClick);
    this.drawer.addEventListener('click', this.boundBackdropClick);
    this.closeButtons.forEach((button) => {
      button.addEventListener('click', this.boundClose);
    });

    this.initialized = true;
  }

  disconnectedCallback() {
    document.removeEventListener('keyup', this.boundKeyup);
    this.trigger?.removeEventListener('click', this.boundTriggerClick);
    this.drawer?.removeEventListener('click', this.boundBackdropClick);
    this.closeButtons?.forEach((button) => {
      button.removeEventListener('click', this.boundClose);
    });
    removeTrapFocus(this.activeElement || this.trigger || null);

    if (this.drawer?.parentElement === document.body) {
      this.drawer.remove();
    }

    this.initialized = false;
  }

  mountDrawer() {
    const drawerId = this.trigger.getAttribute('aria-controls');
    this.drawer = drawerId ? document.getElementById(drawerId) : null;

    if (!this.drawer) {
      const fragment = this.template.content.cloneNode(true);
      this.drawer = fragment.querySelector('[data-size-guide-drawer]');
      if (!this.drawer) return;
      document.body.appendChild(fragment);
    }

    this.dialog = this.drawer.querySelector('[data-size-guide-panel]');
    this.closeButtons = this.drawer.querySelectorAll('[data-size-guide-close]');
  }

  isOpen() {
    return this.drawer?.classList.contains('active');
  }

  handleTriggerClick(event) {
    event.preventDefault();
    this.isOpen() ? this.close() : this.open(this.trigger);
  }

  open(triggeredBy = this.trigger) {
    if (!this.drawer || !this.dialog || this.isOpen()) return;

    this.activeElement = triggeredBy;
    this.trigger.setAttribute('aria-expanded', 'true');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.addEventListener('keyup', this.boundKeyup);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this.drawer.classList.add('active');
        trapFocus(this.dialog, this.dialog);
      });
    });
  }

  handleKeyup(event) {
    if (event.code.toUpperCase() === 'ESCAPE') this.close();
  }

  handleBackdropClick(event) {
    if (!this.isOpen() || !this.dialog) return;
    if (this.dialog.contains(event.target)) return;
    this.close();
  }

  close() {
    if (!this.drawer || !this.dialog || this.drawer.getAttribute('aria-hidden') === 'true') return;

    this.drawer.classList.remove('active');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keyup', this.boundKeyup);
    removeTrapFocus(this.activeElement || this.trigger);
  }
}

if (!customElements.get('size-guide-drawer')) {
  customElements.define('size-guide-drawer', SizeGuideDrawer);
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.madeleine-product slider-component').forEach((component) => {
    const slider = component.querySelector('[id^="Slider-Gallery-"]');
    const dots = component.querySelectorAll('[data-slide-index]');
    if (!slider || !dots.length) return;

    const updateDots = () => {
      const slides = Array.from(slider.querySelectorAll('.slider__slide')).filter((slide) => slide.clientWidth > 0);
      if (!slides.length) return;

      let activeIndex = 0;
      const sliderCenter = slider.scrollLeft + slider.clientWidth / 2;

      slides.forEach((slide, index) => {
        const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
        if (Math.abs(slideCenter - sliderCenter) < Math.abs((slides[activeIndex]?.offsetLeft || 0) + (slides[activeIndex]?.clientWidth || 0) / 2 - sliderCenter)) {
          activeIndex = index;
        }
      });

      const activeMediaId = slides[activeIndex]?.dataset.mediaId;

      dots.forEach((dot, index) => {
        const isActive = dot.dataset.target ? dot.dataset.target === activeMediaId : index === activeIndex;
        dot.classList.toggle('is-active', isActive);
      });
    };

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const slides = Array.from(slider.querySelectorAll('.slider__slide')).filter((slide) => slide.clientWidth > 0);
        const slide = dot.dataset.target
          ? slides.find((item) => item.dataset.mediaId === dot.dataset.target)
          : slides[Number(dot.dataset.slideIndex)];
        if (!slide) return;
        slider.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      });
    });

    slider.addEventListener('scroll', updateDots, { passive: true });
    window.addEventListener('resize', updateDots);
    updateDots();
  });

  document.querySelectorAll('.madeleine-color-siblings').forEach((wrapper) => {
    const track = wrapper.querySelector('[data-color-siblings-track]');
    const prev = wrapper.querySelector('[data-color-siblings-prev]');
    const next = wrapper.querySelector('[data-color-siblings-next]');
    if (!track || !prev || !next) return;

    const update = () => {
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth - 1);
      prev.classList.toggle('is-hidden', track.scrollLeft <= 2);
      next.classList.toggle('is-hidden', track.scrollLeft >= maxScroll);
    };

    const getStep = () => {
      const item = track.querySelector('.madeleine-color-siblings__item');
      if (!item) return track.clientWidth * 0.8;
      const styles = window.getComputedStyle(track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || '0');
      return item.getBoundingClientRect().width * 4 + gap * 4;
    };

    prev.addEventListener('click', () => {
      track.scrollBy({ left: -getStep(), behavior: 'smooth' });
    });

    next.addEventListener('click', () => {
      track.scrollBy({ left: getStep(), behavior: 'smooth' });
    });

    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  });

  document.querySelectorAll('[data-madeleine-instagram-track]').forEach((track) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (track.dataset.madeleineInstagramReady === 'true') return;
    track.dataset.madeleineInstagramReady = 'true';

    const originalItems = Array.from(track.querySelectorAll('.madeleine-instagram__item'));
    if (originalItems.length <= 1) return;

    const createClone = (item) => {
      const clone = item.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      return clone;
    };

    originalItems
      .map((item) => createClone(item))
      .reverse()
      .forEach((clone) => track.prepend(clone));
    originalItems.map((item) => createClone(item)).forEach((clone) => track.append(clone));

    let paused = false;
    let intervalId;
    let manualPauseTimeout;
    let scrollEndTimeout;

    const getItems = () => Array.from(track.querySelectorAll('.madeleine-instagram__item'));

    const withoutSmoothScroll = (callback) => {
      const previousBehavior = track.style.scrollBehavior;
      track.style.scrollBehavior = 'auto';
      callback();
      window.requestAnimationFrame(() => {
        if (previousBehavior) {
          track.style.scrollBehavior = previousBehavior;
        } else {
          track.style.removeProperty('scroll-behavior');
        }
      });
    };

    const getStep = () => {
      const items = getItems();
      if (items.length < 2) return track.clientWidth;
      return items[1].offsetLeft - items[0].offsetLeft;
    };

    const getLoopMetrics = () => {
      const items = getItems();
      const firstOriginal = items[originalItems.length];
      const firstTrailingClone = items[originalItems.length * 2];
      if (!firstOriginal || !firstTrailingClone) return null;

      return {
        originalStart: firstOriginal.offsetLeft,
        setWidth: firstTrailingClone.offsetLeft - firstOriginal.offsetLeft,
      };
    };

    const normalizePosition = () => {
      const metrics = getLoopMetrics();
      if (!metrics || !metrics.setWidth) return;

      const { originalStart, setWidth } = metrics;
      const minScroll = originalStart - getStep() / 2;
      const maxScroll = originalStart + setWidth - getStep() / 2;

      if (track.scrollLeft < minScroll) {
        withoutSmoothScroll(() => {
          track.scrollLeft += setWidth;
        });
      } else if (track.scrollLeft >= maxScroll) {
        withoutSmoothScroll(() => {
          track.scrollLeft -= setWidth;
        });
      }
    };

    const resetToOriginalSet = () => {
      const metrics = getLoopMetrics();
      if (!metrics) return;
      withoutSmoothScroll(() => {
        track.scrollLeft = metrics.originalStart;
      });
    };

    const goNext = () => {
      track.scrollBy({ left: getStep(), behavior: 'smooth' });
    };

    const pauseTemporarily = () => {
      paused = true;
      window.clearTimeout(manualPauseTimeout);
      manualPauseTimeout = window.setTimeout(() => {
        paused = false;
      }, 1800);
    };

    const handleScroll = () => {
      window.clearTimeout(scrollEndTimeout);
      scrollEndTimeout = window.setTimeout(normalizePosition, 140);
    };

    const startAutoplay = () => {
      window.clearInterval(intervalId);
      intervalId = window.setInterval(() => {
        if (paused) return;
        goNext();
      }, 2500);
    };

    track.addEventListener('pointerenter', () => {
      paused = true;
    });

    track.addEventListener('pointerleave', () => {
      paused = false;
    });

    track.addEventListener('touchstart', pauseTemporarily, { passive: true });
    track.addEventListener('pointerdown', pauseTemporarily, { passive: true });
    track.addEventListener('wheel', pauseTemporarily, { passive: true });
    track.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', normalizePosition);

    resetToOriginalSet();
    startAutoplay();
  });
});
